import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button, ButtonType } from '@src/components/Button'
import { AlertModal } from '@src/components/Modal/AlertModal'
import { NarrowFulfilmentMethodInputType } from '@src/graphql-types'
import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import { CurrentFulfilmentType } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/validation'
import { useCustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { useBasketOutletId } from '@src/hooks/useBasketOutletId'
import {
  CheckoutRoute,
  useCheckoutRouter,
} from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { convertToSlug } from '@src/utils/convertToSlug'

import { apolloClient } from '@src/apolloClient'
import { singleOrderCustomType } from './queries/getSingleOrderCustomType'

export const OrderAgainButton: React.FC<{ myOrder: singleOrderCustomType }> = ({
  myOrder,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation('singleOrder')
  const basketItems = useBasketItems()
  const { urlPath } = useMarketplace()

  const confirmationRequired = !!basketItems.items.length
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const { updateBasketOutletId } = useBasketOutletId()

  const { setCurrentFulfilment } = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })
  const { outlet, groupedItems } = myOrder

  const { data: customerAddressData } = useCustomerDetailsAndAddressesQuery({
    addressAcceptingOrdersToOutletId: outlet.id,
  })

  const checkoutRouter = useCheckoutRouter()
  const reorder = () => {
    setLoading(true)
    // refetch totals to make sure the redeemed loyalty card discount dissapears
    apolloClient
      .refetchQueries({
        include: ['CheckoutBasketTotals'],
      })
      .then(() => {
        basketItems.clear({ shouldResetCheckoutRouter: true })
        // grouped items to basketItems and remove add-on items from reorder
        const newBasketItems = groupedItems
          .filter(item => !item.isAddOnItem)
          .map(item => ({
            name: item.name,
            menuItemId: item.menuItemId,
            outletMenuItemId: `${outlet.id}:${item.menuItemId}`,
            optionItemIds: item.optionItems.map(optionItem => optionItem.id),
            quantity: item.quantity,
            singleItemNotes: item.singleItemNotes ?? '',
          }))

        const { customerDeliveryAddress, narrowFulfilmentMethod } = myOrder

        if (
          narrowFulfilmentMethod === NarrowFulfilmentMethodInputType.DELIVERY
        ) {
          if (!customerDeliveryAddress) {
            throw new Error('Delivery order without delivery address')
          }

          const currentAddresses =
            customerAddressData?.customerDetails?.deliveryAddresses || []

          const addressStillExists = currentAddresses.some(
            address => address.id === customerDeliveryAddress.id
          )

          if (addressStillExists) {
            setCurrentFulfilment({
              deliveryNotes: null,
              orderNotes: myOrder.customerOrderNotes,
              type: CurrentFulfilmentType.DELIVERY_SAVED_ADDRESS,
              addressId: customerDeliveryAddress.id,
            })
          } else {
            // handle if address from order has since been deleted
            setCurrentFulfilment({
              orderNotes: myOrder.customerOrderNotes,
              type: CurrentFulfilmentType.DELIVERY_EVERYWHERE,
            })
            basketItems.addItems({
              items: newBasketItems,
              removeExistingItems: true,
            })

            updateBasketOutletId(outlet.id)
            navigate(
              `/${urlPath}/${outlet.id}/${convertToSlug(
                outlet.displayName
              )}/menu`
            )

            checkoutRouter.override(CheckoutRoute.FULFILMENT)
            return
          }
        } else if (
          narrowFulfilmentMethod === NarrowFulfilmentMethodInputType.COLLECTION
        ) {
          setCurrentFulfilment({
            collectionNotes: null,
            orderNotes: myOrder.customerOrderNotes,
            type: CurrentFulfilmentType.COLLECTION,
          })
        } else if (
          narrowFulfilmentMethod === NarrowFulfilmentMethodInputType.TABLE
        ) {
          setCurrentFulfilment({
            tableNotes: null,
            orderNotes: myOrder.customerOrderNotes,
            type: CurrentFulfilmentType.TABLE_UNSELECTED,
          })
        }

        basketItems.addItems({
          items: newBasketItems,
          removeExistingItems: true,
        })
        updateBasketOutletId(outlet.id)

        navigate(
          `/${urlPath}/${outlet.id}/${convertToSlug(outlet.displayName)}/menu`
        )
        checkoutRouter.override(CheckoutRoute.BASKET)
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(
          'Error clearing the basket and updating fulfilment',
          error
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <AlertModal
        isOpen={confirmationModalOpen}
        title={t('new_order_confirm')}
        action={{
          onClick: () => {
            setConfirmationModalOpen(false)
            reorder()
          },
          text: t('confirm'),
          intent: ButtonType.PRIMARY,
        }}
        cancel={{
          onClick: () => {
            setConfirmationModalOpen(false)
          },
          text: t('cancel'),
          intent: ButtonType.SECONDARY,
        }}
      />

      <Button
        onClick={() => {
          if (confirmationRequired) {
            setConfirmationModalOpen(true)
          } else {
            reorder()
          }
        }}
        loading={loading}
        disabled={!myOrder.outlet.active}
        content={t('order_again')}
      />
    </>
  )
}
